import {AsyncError, Loaded} from 'Core/models/Async';
import {eqValidatableEmail, ValidatableEmail, ValidatedEmail} from 'Core/models/Email';
import {eqStringUnion, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {
  eqValidatableNonEmptyString,
  ValidatableNonEmptyString,
  ValidatedNonEmptyString,
} from 'Core/models/ValidatableNonEmptyString';
import {
  EmptyPhoneNumber,
  eqValidatablePhone,
  isEmptyPhoneNumber,
  ValidatablePhone,
  ValidatedPhoneNumber,
} from 'Core/models/PhoneNumber';
import {isValid} from 'Core/models/Validatable';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {Left} from 'fp-ts/lib/Either';
import {Eq, eqBoolean, eqString, getStructEq} from 'fp-ts/lib/Eq';

export class UnvalidatedVip {
  public readonly key = 'UnvalidatedVip';

  constructor(
    public readonly firstName: StringUnion,
    public readonly lastName: StringUnion,
    public readonly username: ValidatableNonEmptyString,
    public readonly phoneNumber: ValidatablePhone,
    public readonly email: ValidatableEmail,
    public readonly isAhl: boolean,
    public readonly isNewYork: boolean,
    public readonly isExternalAdministrator: boolean,
    public readonly id?: string
  ) {}
}

export class InvalidVip {
  public readonly key = 'InvalidVip';

  public constructor(
    public readonly firstName: StringUnion,
    public readonly lastName: StringUnion,
    public readonly username: ValidatableNonEmptyString,
    public readonly phoneNumber: ValidatablePhone,
    public readonly email: ValidatableEmail,
    public readonly isAhl: boolean,
    public readonly isNewYork: boolean,
    public readonly isExternalAdministrator: boolean,
    public readonly id?: string
  ) {}
}

export class ValidVip {
  public static create(
    firstName: StringUnion,
    lastName: StringUnion,
    username: ValidatableNonEmptyString,
    phoneNumber: ValidatablePhone,
    email: ValidatableEmail,
    isAhl: boolean,
    isNewYork: boolean,
    isExternalAdministrator: boolean,
    id?: string
  ) {
    return firstName instanceof NonEmptyString &&
      lastName instanceof NonEmptyString &&
      isValid(email) &&
      isValid(username) &&
      (isValid(phoneNumber) || isEmptyPhoneNumber(phoneNumber)) &&
      (isAhl || isNewYork)
      ? new ValidVip(firstName, lastName, username, phoneNumber, email, isAhl, isNewYork, isExternalAdministrator, id)
      : new InvalidVip(
          firstName,
          lastName,
          username,
          phoneNumber,
          email,
          isAhl,
          isNewYork,
          isExternalAdministrator,
          id
        );
  }

  public readonly key = 'ValidVip';
  private constructor(
    public readonly firstName: NonEmptyString,
    public readonly lastName: NonEmptyString,
    public readonly username: ValidatedNonEmptyString,
    public readonly phoneNumber: ValidatedPhoneNumber | Loaded<Left<EmptyPhoneNumber>>,
    public readonly email: ValidatedEmail,
    public readonly isAhl: boolean,
    public readonly isNewYork: boolean,
    public readonly isExternalAdministrator: boolean,
    public readonly id?: string
  ) {}
}

export class ErrorVip {
  public readonly key = 'ErrorVip';

  public constructor(
    public readonly firstName: NonEmptyString,
    public readonly lastName: NonEmptyString,
    public readonly username: ValidatedNonEmptyString,
    public readonly phoneNumber: ValidatedPhoneNumber | Loaded<Left<EmptyPhoneNumber>>,
    public readonly email: ValidatedEmail,
    public readonly errors: AsyncError,
    public readonly isAhl: boolean,
    public readonly isNewYork: boolean,
    public readonly isExternalAdministrator: boolean,
    public readonly id?: string
  ) {}
}

export class SavedVip {
  public readonly key = 'SavedVip';

  public constructor(
    public readonly firstName: NonEmptyString,
    public readonly lastName: NonEmptyString,
    public readonly username: ValidatedNonEmptyString,
    public readonly phoneNumber: ValidatedPhoneNumber,
    public readonly email: ValidatedEmail,
    public readonly isAhl: boolean,
    public readonly isNewYork: boolean,
    public readonly isExternalAdministrator: boolean,
    public readonly id: string
  ) {}
}

export type Vip = SavedVip | ErrorVip | ValidVip | InvalidVip | UnvalidatedVip;

export type VipOptions<TReturn> = ToMatchOptions<Vip, 'key', TReturn>;

export const matchVip = <TReturn>(vip: Vip, options: VipOptions<TReturn>): TReturn => {
  switch (vip.key) {
    case 'InvalidVip':
      return options.InvalidVip(vip);
    case 'UnvalidatedVip':
      return options.UnvalidatedVip(vip);
    case 'ValidVip':
      return options.ValidVip(vip);
    case 'ErrorVip':
      return options.ErrorVip(vip);
    case 'SavedVip':
      return options.SavedVip(vip);
  }
};

export const eqVip: Eq<Vip> = getStructEq<Vip>({
  email: eqValidatableEmail,
  firstName: eqStringUnion,
  key: eqString,
  lastName: eqStringUnion,
  phoneNumber: eqValidatablePhone,
  username: eqValidatableNonEmptyString,
  isAhl: eqBoolean,
  isNewYork: eqBoolean,
  isExternalAdministrator: eqBoolean,
});

export const validateVip = (vip: Vip) =>
  ValidVip.create(
    vip.firstName,
    vip.lastName,
    vip.username,
    vip.phoneNumber,
    vip.email,
    vip.isAhl,
    vip.isNewYork,
    vip.isExternalAdministrator,
    vip.id
  );
